import type React from 'react'

import {
  type SpriteMap,
  SpriteMap16,
  type SpriteMapCustom,
  type SpriteMapPredefined
} from '../global'
import { createVariants } from '../lib'

export enum IconRotation {
  Deg0 = '0',
  Deg90 = '90',
  Deg180 = '180',
  Deg270 = '270'
}

// Circle icons draw their outer ring flush to the viewBox edge, so an svg that clips its own box
// shaves a sliver off the ring whenever the box lands on a fractional pixel.
const iconClasses = createVariants({
  base: 'overflow-visible',
  variants: {
    rotation: {
      [IconRotation.Deg0]: '',
      [IconRotation.Deg90]: 'rotate-90',
      [IconRotation.Deg180]: 'rotate-180',
      [IconRotation.Deg270]: '-rotate-90'
    }
  },
  defaultVariants: {
    rotation: IconRotation.Deg0
  }
})

type IconProps = {
  icon: SpriteMap | SpriteMap16 | SpriteMapCustom | SpriteMapPredefined
  rotation?: IconRotation
  className?: string
  // Intersect with CSSProperties so the object stays assignable to the svg `style` prop without
  // relying on a third-party module augmentation to widen CSSProperties with a custom-property
  // index signature.
  style?: React.CSSProperties & {
    '--icon-outline'?: string
    '--icon-stroke'?: string
    '--icon-fill'?: string
  }
}

export const Icon: React.FC<IconProps> = ({ icon, rotation, className, style }) => {
  const is16Px = Object.values<string>(SpriteMap16).includes(icon)
  return (
    <svg
      width={is16Px ? 16 : 24}
      height={is16Px ? 16 : 24}
      viewBox={is16Px ? '0 0 16 16' : '0 0 24 24'}
      className={iconClasses({ rotation, className })}
      style={style}
    >
      <use href={`#${icon}`} />
    </svg>
  )
}
